import { InfoTooltip } from '../InfoTooltip.js';
import { METRIC_EXPLANATIONS } from '../../data/metric-explanations.js';

export interface LiquidityIntelData {
  locked: boolean;
  lockPlatform: string | null;
  lockMonths: number | null;
  unlockTime: string | null;
  unlockRisk: string;
  currentLiquidityUsd: number | null;
  liquidityChange24h: number | null;
  // Provider-level fields
  liquidityUsd?: number | null;
  liquidityPairAgeDays?: number | null;
  contractAgeDays?: number | null;
  pairCreatedAt?: number | null;
  scamIndicators?: string[];
  liquidityRiskLevel?: string | null;
  dataUnavailableReason?: string;
}

const NO_DATA = 'No data available yet';

function riskColor(level: string): string {
  if (level === 'HIGH') return '#e74c3c';
  if (level === 'MEDIUM') return '#f39c12';
  return '#27ae60';
}

function fmtUsd(v: number | null | undefined): string {
  if (v == null) return NO_DATA;
  return '$' + v.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

export function LiquidityIntelPanel({ data }: { data: LiquidityIntelData | null | undefined }) {
  const d = data ?? null;

  if (!d) return null;

  if (d.dataUnavailableReason) {
    return (
      <div className="dash-card ws-forensic-section">
        <h3 className="ws-forensic-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          Liquidity Intelligence
          <InfoTooltip explain={METRIC_EXPLANATIONS.lpLock} compact />
        </h3>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Liquidity Analysis</span>
            <span className="intel-metric-value" style={{ fontSize: '0.85rem' }}>
              {d.dataUnavailableReason}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dash-card ws-forensic-section">
      <h3 className="ws-forensic-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        Liquidity Intelligence
        <InfoTooltip explain={METRIC_EXPLANATIONS.lpLock} compact />
      </h3>
      <div className="intel-grid intel-grid-3">
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">LP Locked</span>
            <span className={`intel-bool-badge ${d.locked ? 'intel-bool-good' : 'intel-bool-bad'}`}>
              {d.locked ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Unlock Risk</span>
            <span className="intel-metric-value" style={{ color: riskColor(d.unlockRisk) }}>
              {d.unlockRisk === 'LOW' ? 'Low Risk Indicators' : d.unlockRisk === 'HIGH' ? 'Severe Risk Indicators' : 'Moderate Risk Indicators'}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Lock Platform</span>
            <span className="intel-metric-value">
              {d.lockPlatform ?? NO_DATA}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Lock Duration</span>
            <span className="intel-metric-value">
              {d.lockMonths != null ? `${d.lockMonths} months` : NO_DATA}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">
              Current Liquidity
              <InfoTooltip explain={METRIC_EXPLANATIONS.liquidity} compact />
            </span>
            <span className="intel-metric-value">
              {fmtUsd(d.currentLiquidityUsd)}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Unlock Date</span>
            <span className="intel-metric-value">
              {d.unlockTime ? new Date(d.unlockTime).toLocaleDateString() : NO_DATA}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">DEX Liquidity</span>
            <span className="intel-metric-value">
              {d.liquidityUsd != null ? fmtUsd(d.liquidityUsd) : NO_DATA}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">
              Contract Age
              <InfoTooltip explain={METRIC_EXPLANATIONS.contractAge} compact />
            </span>
            <span className="intel-metric-value">
              {d.contractAgeDays != null ? `${Math.round(d.contractAgeDays)} days` : NO_DATA}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">
              Top Liquidity Pair Age
              <InfoTooltip explain={METRIC_EXPLANATIONS.pairAge} compact />
            </span>
            <span className="intel-metric-value">
              {d.liquidityPairAgeDays != null ? `${Math.round(d.liquidityPairAgeDays)} days` : NO_DATA}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Pair Created</span>
            <span className="intel-metric-value" style={{ fontSize: '0.8rem' }}>
              {d.pairCreatedAt ? new Date(d.pairCreatedAt).toLocaleDateString() : NO_DATA}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Liquidity Risk</span>
            {d.liquidityRiskLevel ? (
              <span className={`intel-bool-badge ${d.liquidityRiskLevel === 'LOW' ? 'intel-bool-good' : 'intel-bool-bad'}`}>
                {d.liquidityRiskLevel === 'LOW' ? 'Low Risk Indicators' : d.liquidityRiskLevel === 'HIGH' ? 'Severe Risk Indicators' : 'Moderate Risk Indicators'}
              </span>
            ) : (
              <span className="intel-metric-value">{NO_DATA}</span>
            )}
          </div>
        </div>
      </div>
      {d.scamIndicators && d.scamIndicators.length > 0 && (
        <ul style={{ margin: '0.5rem 0 0', padding: '0 0 0 1.2rem', fontSize: '0.8rem', color: '#e74c3c' }}>
          {d.scamIndicators.map((s, i) => <li key={i}>{s}</li>)}
        </ul>
      )}
    </div>
  );
}
